import {
  DataGrid,
  GridCellValue,
  GridColDef,
  GridRowParams,
  GridSelectionModel,
} from "@mui/x-data-grid";
import {
  differenceBetweenDateStringsInDays,
  hasDatePassed,
  truncateISOString,
} from "../../utils/dateTime";
import { CircularProgress, Grid } from "@mui/material";
import { Item } from "../../utils/sharedTypes";
import { SoftwareSoldItemFilter } from "./softwareSoldTypes";
import { useHardwareTypeMap } from "../../hooks/useHardwareTypeMap";
import SupportPurchasesButton from "../support-purchases/SupportPurchasesButton";
import { useFindAllLicensesQuery } from "../../api/api-hooks";
import { License } from "../../api/api-generated";
import { useGetMostRecentSupportPurchase } from "./useGetMostRecentSupportPurchase";
import { useGetLicenseFilter } from "./useGetLicenseFilter";
import SoftwareSoldModulesButton from "./SoftwareSoldModulesButton";
import { useGridSort } from "../../hooks/useGridSort";
import React from "react";
import SystemModulesButton from "../system-module-licenses/SystemModulesButton";

interface Props {
  setSelectedSoftware: React.Dispatch<React.SetStateAction<Item[]>>;
  filters: SoftwareSoldItemFilter;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const SoftwareSoldTable = ({
  setSelectedSoftware,
  filters,
  setEditModalOpenFlag,
}: Props) => {
  const { data: licenses } = useFindAllLicensesQuery({});
  const typeMap = useHardwareTypeMap();
  const { sortModel, handleSortModelChange } = useGridSort("id", "desc");
  const filterLicenseItem = useGetLicenseFilter();
  const getMostRecentSupportPurchase = useGetMostRecentSupportPurchase();
  if (licenses === undefined || typeMap === undefined)
    return <CircularProgress></CircularProgress>;
  // Define data for display in DataGrid
  const columns: GridColDef[] = [
    { field: "id", hide: true },
    { field: "companyName", headerName: "Company", width: 150 },
    { field: "analyzerType", headerName: "Hardware Type", width: 180 },
    { field: "serial", headerName: "Serial", width: 150 },
    { field: "softwareType", headerName: "Software Type", width: 170 },
    { field: "soldDate", headerName: "Sold Date", width: 150 },
    { field: "supportStatus", headerName: "Support Status", width: 180 },
    {
      field: "buttons",
      headerName: " ",
      width: 400,
      renderCell: (params) => {
        const licenseId: GridCellValue = params.getValue(params.id, "id");
        const clickedLicense = licenses.find(
          (license) => license.id === licenseId
        );
        if (!clickedLicense) return "";
        return (
          <Grid container spacing={1}>
            <Grid item>
              <SupportPurchasesButton license={clickedLicense} />
            </Grid>
            {clickedLicense.software.type
              .toLowerCase()
              .includes("spectras") && (
              <>
                <Grid item>
                  <SoftwareSoldModulesButton license={clickedLicense} />
                </Grid>
                <Grid item>
                  <SystemModulesButton company={clickedLicense.company} />
                </Grid>
              </>
            )}
          </Grid>
        );
      },
    },
  ];
  const rows = licenses
    .filter((item: License) => !item.module && filterLicenseItem(item, filters))
    .map((item: License) => {
      const currentSupportRecord = getMostRecentSupportPurchase(item);
      return {
        ...item,
        companyName: item.company.companyName,
        analyzerType: item.hardware
          ? typeMap.get(item.hardware.hardwareTypeId)
          : "None",
        softwareType: item.software.type,
        soldDate: !item.order ? "NA" : truncateISOString(item.order.orderDate),
        supportStartDate:
          !currentSupportRecord ||
          currentSupportRecord.supportStartDate === null
            ? "None"
            : truncateISOString(currentSupportRecord.supportStartDate),
        supportDuration:
          !currentSupportRecord ||
          currentSupportRecord.supportEndDate === null ||
          currentSupportRecord.supportStartDate === null
            ? "None"
            : differenceBetweenDateStringsInDays(
                currentSupportRecord.supportEndDate,
                currentSupportRecord.supportStartDate
              ) + " Days",
        serial: item.hardware ? item.hardware.serial : "None",
        supportStatus:
          !currentSupportRecord || currentSupportRecord.supportEndDate === null
            ? "None"
            : hasDatePassed(new Date(currentSupportRecord.supportEndDate))
            ? "Expired"
            : "Active",
      };
    });
  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedSoftware(
      licenses.filter((license) => model.includes(license.id))
    );
  };
  const handleRowClick = (params: GridRowParams) => {
    setSelectedSoftware(
      licenses.filter((license: License) => license.id === params.row.id)
    );
    setEditModalOpenFlag(true);
  };
  return (
    <div style={{ paddingBottom: "1rem", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={100}
        checkboxSelection
        disableSelectionOnClick
        autoHeight
        disableColumnMenu={true}
        onSelectionModelChange={handleTableSelection}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default SoftwareSoldTable;
